import { Box, ClickAwayListener, Fade, IconButton, TextField, Tooltip } from '@mui/material';
import { StaticTimePicker, StaticTimePickerProps } from '@mui/x-date-pickers';
import { omit } from 'lodash';
import moment, { Moment } from 'moment';
import { useState, useRef, useEffect } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';
type Props = {
    onChange?(value: Date): void;
    value: Date;
    timePickerProps?: StaticTimePickerProps<Moment>;
    width?: React.CSSProperties['width'];
    marginTop?: React.CSSProperties['marginTop'];
    left?: React.CSSProperties['left'];
    zIndex?: React.CSSProperties['zIndex'];
};
export default function BaseTimePicker(props: Props) {
    const [open, setOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [tooltipTime, setTooltipTime] = useState('');
    const clockRef = useRef<HTMLDivElement>(null);
    const pickerBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) {
            setIsDragging(false);
            setTooltipTime('');
        }
    }, [open]);

    const calculateTimeFromPosition = (clientX: number, clientY: number, isHourView: boolean) => {
        const clockFace = clockRef.current?.querySelector('.MuiClock-clock');
        if (!clockFace) return null;

        const rect = clockFace.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const dx = clientX - centerX;
        const dy = clientY - centerY;

        let angle = Math.atan2(dx, -dy) * (180 / Math.PI);
        if (angle < 0) angle += 360;

        if (isHourView) {
            const hour = Math.round(angle / 15) % 24;
            return hour;
        } else {
            const minute = Math.round(angle / 6) % 60;
            return minute;
        }
    };

    const handlePointerDown = (e: PointerEvent) => {
        if (!clockRef.current) return;

        const target = e.target as HTMLElement;
        const isClockElement = target.closest('.MuiClock-clock, .MuiClockPointer-root, .MuiClockNumber-root');

        if (isClockElement) {
            const isHourView = !!clockRef.current.querySelector('.MuiClock-clock [aria-label*="hour"]');

            if (!isHourView) {
                setIsDragging(true);
                updateTooltip(e.clientX, e.clientY);
            }
        }
    };

    const updateTooltip = (clientX: number, clientY: number) => {
        if (!clockRef.current) return;

        const isHourView = !!clockRef.current.querySelector('.MuiClock-clock [aria-label*="hour"]');

        if (isHourView) {
            setIsDragging(false);
            setTooltipTime('');
            return;
        }

        const currentValue = props.value ? moment(props.value) : moment();
        const calculatedValue = calculateTimeFromPosition(clientX, clientY, false);

        if (calculatedValue !== null) {
            const displayTime = `${String(currentValue.hour()).padStart(2, '0')}:${String(calculatedValue).padStart(2, '0')}`;
            setTooltipTime(displayTime);
            setTooltipPosition({ x: clientX, y: clientY });
        }
    };

    const handlePointerMove = (e: PointerEvent) => {
        if (!isDragging) return;
        updateTooltip(e.clientX, e.clientY);
    };

    const handlePointerUp = () => {
        setIsDragging(false);
        setTooltipTime('');
    };

    useEffect(() => {
        const pickerBox = pickerBoxRef.current;
        if (!pickerBox || !open) return;

        pickerBox.addEventListener('pointerdown', handlePointerDown as any);
        document.addEventListener('pointermove', handlePointerMove as any);
        document.addEventListener('pointerup', handlePointerUp);
        document.addEventListener('pointercancel', handlePointerUp);

        return () => {
            pickerBox.removeEventListener('pointerdown', handlePointerDown as any);
            document.removeEventListener('pointermove', handlePointerMove as any);
            document.removeEventListener('pointerup', handlePointerUp);
            document.removeEventListener('pointercancel', handlePointerUp);
        };
    }, [open, props.value, isDragging]);

    return (
        <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={() => setOpen(false)}>
            <Box sx={{ position: 'relative', zIndex: props.zIndex, width: `${props.width ?? '140px'} !important` }}>
                <TextField
                    value={!!props.value ? moment(props.value).format('HH:mm') : 'hh:mm'}
                    sx={{ width: '100%' }}
                    InputProps={{
                        endAdornment: (
                            <IconButton onClick={() => setOpen(true)}>
                                <AiOutlineClockCircle />
                            </IconButton>
                        ),
                        readOnly: true,
                    }}
                />
                <Fade in={!!open}>
                    <Box
                        ref={pickerBoxRef}
                        sx={{
                            background: '#fff',
                            borderRadius: 1,
                            position: 'absolute',
                            top: props.marginTop,
                            border: '1px solid #DDDDDD',
                            left: props.left ?? 0,
                        }}
                    >
                        <div ref={clockRef}>
                            <StaticTimePicker
                                key={props.value ? 'has-value' : 'no-value'}
                                ampm={false}
                                value={props.value ? (props.value as any) : null}
                                displayStaticWrapperAs="desktop"
                                onChange={(value, text) => {
                                    props.onChange?.(value! as any);
                                }}
                                minutesStep={1}
                                onAccept={() => {
                                    setOpen(false);
                                }}
                                {...omit(props.timePickerProps, 'onChange')}
                            />
                        </div>
                    </Box>
                </Fade>
                {isDragging && tooltipTime && (
                    <Tooltip
                        open={true}
                        title={tooltipTime}
                        placement="top"
                        arrow
                        PopperProps={{
                            anchorEl: {
                                getBoundingClientRect: () => ({
                                    top: tooltipPosition.y,
                                    left: tooltipPosition.x,
                                    right: tooltipPosition.x,
                                    bottom: tooltipPosition.y,
                                    width: 0,
                                    height: 0,
                                    x: tooltipPosition.x,
                                    y: tooltipPosition.y,
                                    toJSON: () => {},
                                }),
                            } as any,
                            sx: {
                                pointerEvents: 'none',
                            },
                        }}
                    >
                        <span />
                    </Tooltip>
                )}
            </Box>
        </ClickAwayListener>
    );
}
